// Floor plan editor. Plain DOM and SVG, mounted into a React page by FloorPlanEditor.
// Units are metres throughout. The plan is one JSON object:
// { v, meta, scan, walls[{id,pts,t,status,kind}], doors[], labels[], notes[] }
import './editor.css';

const STATUS = {
  existing: { name: 'Existing', col: '#393C5B' },
  new: { name: 'New', col: '#2F8F5B' },
  remove: { name: 'Remove', col: '#B23A3A' },
};
const NOTE_COL = { amber: '#E6BE8A', blue: '#5A7BB5', red: '#B23A3A' };
const NOTE_INK = { amber: '#393C5B', blue: '#FFFFFF', red: '#FFFFFF' };
const NOTE_NAME = { amber: 'Gold', blue: 'Blue', red: 'Red' };
const SELC = '#B9823C';
const CANVAS = '#FBFAF8';

const LIVE_PAL = { existing: '#393C5B', new: '#2F8F5B', remove: '#B23A3A', door: '#393C5B', cut: CANVAS, label: '#393C5B', labelSub: '#969696', noteBg: '#FFFFFF', noteFg: '#393C5B', pinRing: '#FFFFFF', font: 'Jost, Helvetica, Arial, sans-serif', thin: 0.03 };
const PRINT_PAL = { existing: '#1D2033', new: '#1F7A4A', remove: '#B23A3A', door: '#1D2033', cut: '#FFFFFF', label: '#393C5B', labelSub: '#6B6B6B', noteBg: '#FFFDF8', noteFg: '#1D2033', pinRing: '#FFFFFF', font: 'Arial, Helvetica, sans-serif', thin: 0.03 };

const ICONS = {
  back: '<path d="M15 18l-6-6 6-6"/>',
  select: '<path d="M5 3l14 8-6 1.6L10 19z"/>',
  wall: '<path d="M4 18V7h9v6h7"/><circle cx="4" cy="18" r="1.4"/><circle cx="20" cy="13" r="1.4"/>',
  door: '<path d="M3 20h4M17 20h4M7 20V6"/><path d="M7 6a14 14 0 0 1 10 14"/>',
  label: '<path d="M5 6V4h14v2M12 4v16M9 20h6"/>',
  note: '<path d="M4 5h16v11h-9l-5 4v-4H4z"/><path d="M8 9h8M8 12h5"/>',
  measure: '<path d="M3 15l12-12 6 6-12 12z"/><path d="M7 11l2 2M10 8l2 2M13 5l2 2"/>',
  pan: '<path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3"/>',
};
const ico = k => `<svg viewBox="0 0 24 24">${ICONS[k]}</svg>`;

const uid = p => p + Math.random().toString(36).slice(2, 8);
const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const r3 = v => Math.round(v * 1000) / 1000;
const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const byId = (arr, id) => arr.find(o => o.id === id);
function projSeg(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1], L = dx * dx + dy * dy;
  let t = L ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L : 0;
  t = Math.max(0, Math.min(1, t));
  const q = [a[0] + t * dx, a[1] + t * dy];
  return { t, q, d: dist(p, q) };
}
function wallLen(w) { let s = 0; for (let i = 1; i < w.pts.length; i++) s += dist(w.pts[i - 1], w.pts[i]); return s; }
function wrap(text, n) {
  const out = [];
  String(text || '').split('\n').forEach(par => {
    let line = '';
    par.split(/\s+/).forEach(word => {
      if (!word) return;
      if ((line + ' ' + word).trim().length > n && line) { out.push(line); line = word; }
      else line = (line ? line + ' ' : '') + word;
    });
    out.push(line);
  });
  return out.length ? out : [''];
}
function store(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* storage blocked */ } }
function load(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
function unstore(k) { try { localStorage.removeItem(k); } catch (e) { /* storage blocked */ } }

export function emptyPlan(meta, scan) {
  return { v: 1, meta: { project: '', client: '', site: '', drawing: 'Proposed layout', rev: 'A', drawnBy: 'Crossman & Walsh', date: '', note: '', ...meta }, scan: scan || null, walls: [], doors: [], labels: [], notes: [] };
}

export function normalisePlan(p) {
  p.walls = p.walls || []; p.doors = p.doors || []; p.labels = p.labels || []; p.notes = p.notes || []; p.meta = p.meta || {};
  p.walls.forEach(w => { w.status = w.status || 'existing'; w.t = w.t || 0.12; });
  p.doors.forEach(d => { if (d.hx == null) d.hx = 1; if (d.t == null) d.t = 0.36; });
  return p;
}

/**
 * mountEditor(root, opts)
 *  opts.plan      plan object (will be copied)
 *  opts.scanUrl   object URL of the scan image, or null
 *  opts.canEdit   false for view-only
 *  opts.draftKey  localStorage key for unsaved work
 *  opts.baseStamp version stamp of the loaded plan (drafts only restore onto the same version)
 *  opts.onSave(plan) -> Promise resolving { stamp }; reject with { status } (409 conflict, 403 view only)
 *  opts.onBack()
 * Returns { destroy, isDirty }.
 */
export function mountEditor(root, opts) {
  const canEdit = opts.canEdit !== false;
  root.classList.add('fp');
  root.innerHTML = `
  <header class="fp-top">
    <button class="fp-icon-btn" data-act="back" title="Back to floor plans" aria-label="Back to floor plans">${ico('back')}</button>
    <div class="fp-ttl"><b data-el="title"></b><span data-el="sub"></span></div>
    <div class="fp-spacer"></div>
    <span class="fp-status" data-el="status"></span>
    <button class="fp-btn fp-ghost fp-hide-sm" data-act="undo" title="Undo (Ctrl+Z)">Undo</button>
    <button class="fp-btn fp-ghost fp-hide-sm" data-act="redo" title="Redo (Ctrl+Y)">Redo</button>
    <div class="fp-menu">
      <button class="fp-btn" data-act="export" aria-haspopup="true" aria-expanded="false">Export</button>
      <div class="fp-menu-list" data-el="mexp" hidden>
        <button data-x="print">Print on A3, fill the page<small>Biggest the plan will go; or save as an A3 PDF</small></button>
        <button data-x="print-std">Print on A3 at a standard scale<small>1:50, 1:100 and so on, for measuring off</small></button>
        <button data-x="png">Drawing as PNG<small>For WhatsApp or email to site</small></button>
        <button data-x="svg">Drawing as SVG<small>Sharp at any size, prints to A3</small></button>
        <button data-x="json">Backup file (.json)<small>The drawing data, to restore later</small></button>
        ${canEdit ? '<button data-x="import">Restore from backup…<small>Replaces the current drawing</small></button>' : ''}
      </div>
    </div>
    <button class="fp-btn fp-sheet-toggle" data-act="sheet" aria-expanded="false">Details</button>
    ${canEdit ? '<button class="fp-btn fp-primary" data-act="save" title="Save (Ctrl+S)">Save</button>' : ''}
    <input type="file" data-el="fimport" accept=".json,application/json" hidden>
  </header>
  <nav class="fp-rail" aria-label="Tools">
    <button class="fp-tool" data-tool="select" title="Select and move (V)">${ico('select')}<kbd>V</kbd></button>
    ${canEdit ? `
    <button class="fp-tool" data-tool="wall" title="Draw wall (W)">${ico('wall')}<kbd>W</kbd></button>
    <button class="fp-tool" data-tool="door" title="Add door (D)">${ico('door')}<kbd>D</kbd></button>
    <button class="fp-tool" data-tool="label" title="Room label (T)">${ico('label')}<kbd>T</kbd></button>
    <button class="fp-tool" data-tool="note" title="Note on drawing (N)">${ico('note')}<kbd>N</kbd></button>` : ''}
    <button class="fp-tool" data-tool="measure" title="Measure (M)">${ico('measure')}<kbd>M</kbd></button>
    <hr>
    <button class="fp-tool" data-tool="pan" title="Pan (hold Space)">${ico('pan')}</button>
  </nav>
  <main class="fp-stage" data-el="stage">
    <svg class="fp-svg" data-el="svg" xmlns="http://www.w3.org/2000/svg" aria-label="Floor plan drawing">
      <defs>
        <pattern id="fpGridMinor" width="0.5" height="0.5" patternUnits="userSpaceOnUse"><path d="M0.5 0H0V0.5" fill="none" stroke="rgba(57,60,91,.07)" stroke-width="0.012"/></pattern>
        <pattern id="fpGridMajor" width="5" height="5" patternUnits="userSpaceOnUse"><rect width="5" height="5" fill="url(#fpGridMinor)"/><path d="M5 0H0V5" fill="none" stroke="rgba(57,60,91,.16)" stroke-width="0.025"/></pattern>
      </defs>
      <g data-el="world"></g>
    </svg>
    <div class="fp-banner" data-el="banner" hidden><span data-el="bannerText"></span><button class="fp-btn" data-el="bannerA"></button><button class="fp-btn fp-ghost" data-el="bannerB">Dismiss</button></div>
    <div class="fp-hint" data-el="hint"></div>
    <div class="fp-zoom"><button class="fp-btn" data-act="zout" aria-label="Zoom out">−</button><button class="fp-btn" data-act="zfit">Fit</button><button class="fp-btn" data-act="zin" aria-label="Zoom in">+</button></div>
  </main>
  <aside class="fp-side" data-el="side" aria-label="Details"></aside>
  <div class="fp-modal" data-el="modal" hidden><div class="fp-modal-card"><h3 data-el="modalTitle"></h3><div data-el="modalBody"></div><div class="fp-row" style="justify-content:flex-end"><button class="fp-btn" data-act="modalClose">Close</button></div></div></div>`;

  const el = n => root.querySelector(`[data-el="${n}"]`);
  const act = n => root.querySelector(`[data-act="${n}"]`);
  const svg = el('svg'), world = el('world'), side = el('side');

  let plan = normalisePlan(JSON.parse(JSON.stringify(opts.plan)));
  let baseStamp = opts.baseStamp || '';
  let dirty = false, readOnly = !canEdit, lastSaved = '', saving = false, autoT = 0, flashT = 0;
  const view = { tx: 0, ty: 0, k: 30 };
  let tool = 'select', sel = null, hover = null, draft = null, measure = null, drag = null;
  let undo = [], redo = [], userView = false, liveBefore = null;
  const pointers = new Map(); let pinch = null; let spaceDown = false;
  const opt = { dims: false, grid: true, snap: true, invert: false, calib: false };
  try { Object.assign(opt, JSON.parse(load('cw-fp-opts') || '{}')); } catch (e) { /* ignore */ }
  const scanHref = opts.scanUrl || '';

  /* ---------- history ---------- */
  const snapshot = () => JSON.stringify(plan);
  function pushUndo(s) { undo.push(s); if (undo.length > 200) undo.shift(); redo = []; }
  function mutate(fn) { const before = snapshot(); fn(); if (snapshot() !== before) { pushUndo(before); changed(); } else render(); }
  function changed() { dirty = true; if (opts.draftKey) store(opts.draftKey, JSON.stringify({ base: baseStamp, plan })); setStatus(); render(); scheduleAutosave(); }
  function doUndo() { if (!undo.length) return; redo.push(snapshot()); plan = JSON.parse(undo.pop()); validateSel(); changed(); }
  function doRedo() { if (!redo.length) return; undo.push(snapshot()); plan = JSON.parse(redo.pop()); validateSel(); changed(); }
  function validateSel() { if (sel && !findSel()) sel = null; }
  function findSel() { if (!sel) return null; const m = { wall: 'walls', door: 'doors', label: 'labels', note: 'notes' }[sel.type]; return m ? byId(plan[m], sel.id) : null; }

  function nearestWall(p) {
    let best = { d: 1e9, t: 0.3, w: null, q: p, a: 0 };
    plan.walls.forEach(w => {
      for (let i = 1; i < w.pts.length; i++) {
        const r = projSeg(p, w.pts[i - 1], w.pts[i]);
        if (r.d < best.d) { const a = w.pts[i - 1], b = w.pts[i]; best = { d: r.d, t: w.t, w, q: r.q, a: Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI }; }
      }
    });
    return best;
  }
  // every wall corner sitting on one of these points, so joined walls move together
  function linked(points, own, ownIdx) {
    const out = [];
    plan.walls.forEach(w => w.pts.forEach((q, i) => {
      const isOwn = w === own && ownIdx.includes(i);
      if (isOwn || points.some(p => dist(p, q) < 0.015)) out.push({ w, i, o: [q[0], q[1]] });
    }));
    return out;
  }

  /* ---------- coordinates ---------- */
  function toWorld(e) { const r = svg.getBoundingClientRect(); return [(e.clientX - r.left - view.tx) / view.k, (e.clientY - r.top - view.ty) / view.k]; }
  function snapPt(p, { exclude = null, from = null, shift = false, skip = null } = {}) {
    if (from && shift) {
      const dx = p[0] - from[0], dy = p[1] - from[1], L = Math.hypot(dx, dy), a = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
      const Ls = opt.snap ? Math.round(L / 0.05) * 0.05 : L;
      return [r3(from[0] + Math.cos(a) * Ls), r3(from[1] + Math.sin(a) * Ls)];
    }
    if (!opt.snap) return [r3(p[0]), r3(p[1])];
    const tol = 10 / view.k; let best = null, bd = tol;
    plan.walls.forEach(w => w.pts.forEach((q, i) => {
      if (exclude && exclude.id === w.id && (exclude.i == null || exclude.i === i)) return;
      if (skip && skip.some(l => l.w === w && l.i === i)) return;
      const d = dist(p, q); if (d < bd) { bd = d; best = q; }
    }));
    if (draft) draft.pts.forEach(q => { const d = dist(p, q); if (d < bd && q !== draft.pts[draft.pts.length - 1]) { bd = d; best = q; } });
    if (best) return [best[0], best[1]];
    return [r3(Math.round(p[0] / 0.05) * 0.05), r3(Math.round(p[1] / 0.05) * 0.05)];
  }

  /* ---------- drawing ---------- */
  function wallSVG(w, pal, live) {
    const pts = w.pts.map(p => p.join(',')).join(' ');
    const col = pal[w.status] || pal.existing;
    const dash = w.status === 'remove' ? ` stroke-dasharray="${r3(w.t * 1.6)} ${r3(w.t * 1.1)}"` : '';
    let s = `<polyline points="${pts}" fill="none" stroke="${col}" stroke-width="${w.t}" stroke-linejoin="miter" stroke-miterlimit="6" stroke-linecap="square"${dash}/>`;
    if (live) s += `<polyline points="${pts}" fill="none" stroke="transparent" stroke-width="${Math.max(w.t, 14 / view.k)}" data-k="wall" data-id="${w.id}" style="cursor:pointer"/>`;
    return s;
  }
  function doorSVG(d, pal, live) {
    const w = d.w, h = w / 2, t = d.t || 0.34, sy = d.flip ? -1 : 1, sw = pal.thin;
    let g = `<g transform="translate(${d.x} ${d.y}) rotate(${d.a}) scale(${d.hx || 1} ${sy})">`;
    g += `<rect x="${-h}" y="${-t / 2}" width="${w}" height="${t}" fill="${pal.cut}"/>`;
    g += `<line x1="${-h}" y1="${-t / 2}" x2="${-h}" y2="${t / 2}" stroke="${pal.door}" stroke-width="${sw}"/><line x1="${h}" y1="${-t / 2}" x2="${h}" y2="${t / 2}" stroke="${pal.door}" stroke-width="${sw}"/>`;
    if (d.style !== 'opening') {
      g += `<line x1="${-h}" y1="0" x2="${-h}" y2="${-w}" stroke="${pal.door}" stroke-width="${sw * 1.6}"/>`;
      g += `<path d="M ${-h} ${-w} A ${w} ${w} 0 0 1 ${h} 0" fill="none" stroke="${pal.door}" stroke-width="${sw}" stroke-dasharray="${sw * 4} ${sw * 3}"/>`;
    }
    if (live) g += `<rect x="${-h}" y="${d.style === 'opening' ? -t : -w}" width="${w}" height="${d.style === 'opening' ? t * 2 : w + t / 2}" fill="transparent" data-k="door" data-id="${d.id}" style="cursor:move"/>`;
    return g + '</g>';
  }
  const labelW = l => Math.max(l.text.length, (l.sub || '').length) * 0.18;
  function labelSVG(l, pal, live) {
    let s = `<g ${live ? `data-k="label" data-id="${l.id}" style="cursor:move"` : ''}>`;
    s += `<text x="${l.x}" y="${l.y}" text-anchor="middle" font-family="${pal.font}" font-weight="500" font-size="0.3" fill="${pal.label}">${esc(l.text)}</text>`;
    if (l.sub) s += `<text x="${l.x}" y="${r3(l.y + 0.3)}" text-anchor="middle" font-family="${pal.font}" font-size="0.22" fill="${pal.labelSub}">${esc(l.sub)}</text>`;
    if (live) s += `<rect x="${l.x - labelW(l) / 2 - 0.1}" y="${l.y - 0.3}" width="${labelW(l) + 0.2}" height="${l.sub ? 0.7 : 0.42}" fill="transparent"/>`;
    return s + '</g>';
  }
  function noteBox(n) {
    const fs = 0.24, lines = n.hide ? [] : wrap(n.text || 'New note', 32);
    const w = Math.max(1.4, Math.max(...lines.map(s => s.length), 6) * fs * 0.55 + 0.36), h = lines.length * fs * 1.35 + 0.3;
    return { fs, lines, w, h };
  }
  function noteSVG(n, idx, pal, live, r) {
    const key = NOTE_COL[n.color] ? n.color : 'amber', col = NOTE_COL[key];
    let s = '';
    if (!n.hide) {
      const b = noteBox(n), cx = Math.max(n.tx, Math.min(n.x, n.tx + b.w)), cy = Math.max(n.ty, Math.min(n.y, n.ty + b.h));
      s += `<line x1="${n.x}" y1="${n.y}" x2="${cx}" y2="${cy}" stroke="${col}" stroke-width="${pal.thin * 1.5}"/>`;
      s += `<g ${live ? `data-k="nbox" data-id="${n.id}" style="cursor:move"` : ''}><rect x="${n.tx}" y="${n.ty}" width="${r3(b.w)}" height="${r3(b.h)}" rx="0.06" fill="${pal.noteBg}" stroke="${col}" stroke-width="${pal.thin * 1.5}"/>`;
      s += `<text font-family="${pal.font}" font-size="${b.fs}" fill="${pal.noteFg}">` + b.lines.map((ln, i) => `<tspan x="${r3(n.tx + 0.18)}" y="${r3(n.ty + 0.15 + b.fs * (i + 1) * 1.35 - b.fs * 0.3)}">${esc(ln)}</tspan>`).join('') + '</text></g>';
    }
    s += `<g ${live ? `data-k="pin" data-id="${n.id}" style="cursor:move"` : ''}><circle cx="${n.x}" cy="${n.y}" r="${r}" fill="${col}" stroke="${pal.pinRing}" stroke-width="${r * 0.18}"/><text x="${n.x}" y="${r3(n.y + r * 0.36)}" text-anchor="middle" font-family="${pal.font}" font-weight="600" font-size="${r3(r * 1.05)}" fill="${NOTE_INK[key]}">${idx + 1}</text></g>`;
    return s;
  }
  function dimsSVG(w, isSel) {
    const k = view.k, fs = isSel ? 12 / k : Math.max(0.2, 10 / k); let s = '';
    for (let i = 1; i < w.pts.length; i++) {
      const a = w.pts[i - 1], b = w.pts[i], L = dist(a, b); if (L < 0.15) continue;
      let ang = Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI; if (ang > 90) ang -= 180; if (ang < -90) ang += 180;
      const nx = -(b[1] - a[1]) / L, ny = (b[0] - a[0]) / L, off = w.t / 2 + fs * 0.9;
      const mx = (a[0] + b[0]) / 2 + nx * off, my = (a[1] + b[1]) / 2 + ny * off;
      s += `<text x="${mx}" y="${my}" transform="rotate(${ang} ${mx} ${my})" text-anchor="middle" dominant-baseline="middle" font-family="ui-monospace,Menlo,monospace" font-size="${fs}" fill="${isSel ? SELC : '#6E7190'}" stroke="${CANVAS}" stroke-width="${fs * 0.3}" paint-order="stroke" pointer-events="none">${L.toFixed(2)}</text>`;
    }
    return s;
  }
  function tag(x, y, text) {
    const k = view.k, fs = 12 / k, w = (text.length * 7.4 + 14) / k, h = 20 / k;
    return `<g pointer-events="none"><rect x="${x - w / 2}" y="${y - h / 2}" width="${w}" height="${h}" rx="${4 / k}" fill="#393C5B"/><text x="${x}" y="${y + fs * 0.36}" text-anchor="middle" font-family="ui-monospace,Menlo,monospace" font-weight="700" font-size="${fs}" fill="#E6BE8A">${esc(text)}</text></g>`;
  }
  function overlaySVG() {
    const k = view.k, it = findSel(); let s = '';
    if (it && sel.type === 'wall') {
      s += `<polyline points="${it.pts.map(p => p.join(',')).join(' ')}" fill="none" stroke="${SELC}" stroke-width="${2 / k}" pointer-events="none"/>`;
      if (sel.si && it.pts[sel.si]) { const a = it.pts[sel.si - 1], c = it.pts[sel.si]; s += `<line x1="${a[0]}" y1="${a[1]}" x2="${c[0]}" y2="${c[1]}" stroke="${SELC}" stroke-width="${5 / k}" stroke-linecap="round" pointer-events="none"/>`; }
      if (canEdit) it.pts.forEach((p, i) => { s += `<rect x="${p[0] - 6 / k}" y="${p[1] - 6 / k}" width="${12 / k}" height="${12 / k}" fill="${sel.vi === i ? SELC : '#FFFFFF'}" stroke="${SELC}" stroke-width="${2 / k}" data-k="v" data-id="${it.id}" data-i="${i}" style="cursor:move"/>`; });
    }
    if (it && sel.type === 'door') s += `<circle cx="${it.x}" cy="${it.y}" r="${it.w / 2 + 0.1}" fill="none" stroke="${SELC}" stroke-width="${1.5 / k}" stroke-dasharray="${4 / k} ${3 / k}" pointer-events="none"/>`;
    if (it && sel.type === 'label') s += `<rect x="${it.x - labelW(it) / 2 - 0.15}" y="${it.y - 0.36}" width="${labelW(it) + 0.3}" height="${it.sub ? 0.8 : 0.5}" fill="none" stroke="${SELC}" stroke-width="${1.5 / k}" stroke-dasharray="${4 / k} ${3 / k}" pointer-events="none"/>`;
    if (it && sel.type === 'note') {
      s += `<circle cx="${it.x}" cy="${it.y}" r="${16 / k}" fill="none" stroke="${SELC}" stroke-width="${2 / k}" pointer-events="none"/>`;
      if (!it.hide) { const b = noteBox(it); s += `<rect x="${it.tx - 4 / k}" y="${it.ty - 4 / k}" width="${b.w + 8 / k}" height="${b.h + 8 / k}" fill="none" stroke="${SELC}" stroke-width="${1.5 / k}" stroke-dasharray="${4 / k} ${3 / k}" pointer-events="none"/>`; }
    }
    if (draft && draft.pts.length) {
      const pts = [...draft.pts, draft.cur || draft.pts[draft.pts.length - 1]];
      s += `<polyline points="${pts.map(p => p.join(',')).join(' ')}" fill="none" stroke="${STATUS.new.col}" stroke-width="${draft.t}" stroke-linejoin="miter" stroke-linecap="square" opacity="0.75" pointer-events="none"/>`;
      pts.forEach(p => { s += `<circle cx="${p[0]}" cy="${p[1]}" r="${4 / k}" fill="${SELC}" pointer-events="none"/>`; });
      if (draft.cur) { const a = draft.pts[draft.pts.length - 1], b = draft.cur; s += tag((a[0] + b[0]) / 2, (a[1] + b[1]) / 2 - 14 / k, dist(a, b).toFixed(2) + ' m'); }
    }
    if (measure && measure.b) {
      const { a, b } = measure;
      s += `<line x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}" stroke="${SELC}" stroke-width="${2 / k}" stroke-dasharray="${6 / k} ${4 / k}" pointer-events="none"/>`;
      [a, b].forEach(p => { s += `<circle cx="${p[0]}" cy="${p[1]}" r="${4 / k}" fill="${SELC}" pointer-events="none"/>`; });
      s += tag((a[0] + b[0]) / 2, (a[1] + b[1]) / 2 - 16 / k, dist(a, b).toFixed(2) + ' m');
    }
    if (hover && tool === 'wall' && !draft) s += `<circle cx="${hover[0]}" cy="${hover[1]}" r="${4 / k}" fill="${SELC}" pointer-events="none"/>`;
    return s;
  }

  function render() {
    const k = view.k, P = LIVE_PAL; let s = '';
    world.setAttribute('transform', `matrix(${k} 0 0 ${k} ${view.tx} ${view.ty})`);
    if (opt.grid) s += '<rect x="-30" y="-30" width="110" height="100" fill="url(#fpGridMajor)"/>';
    const sc = plan.scan;
    if (sc && sc.show !== false && scanHref) s += `<image href="${esc(scanHref)}" x="${sc.x}" y="${sc.y}" width="${sc.w}" height="${sc.h}" opacity="${sc.opacity}" preserveAspectRatio="none" style="${opt.invert ? 'filter:invert(1)' : ''}" pointer-events="none"/>`;
    const order = { remove: 0, existing: 1, new: 2 };
    [...plan.walls].sort((a, b) => order[a.status] - order[b.status]).forEach(w => { s += wallSVG(w, P, true); });
    plan.doors.forEach(d => { s += doorSVG(d, P, true); });
    plan.labels.forEach(l => { s += labelSVG(l, P, true); });
    plan.walls.forEach(w => { const isSel = !!(sel && sel.type === 'wall' && sel.id === w.id); if (opt.dims || isSel || w.status === 'new') s += dimsSVG(w, isSel); });
    plan.notes.forEach((n, i) => { s += noteSVG(n, i, P, true, 11 / k); });
    s += overlaySVG();
    world.innerHTML = s;
    renderSide(); renderHint();
    const u = act('undo'), r = act('redo');
    if (u) u.disabled = !undo.length || readOnly;
    if (r) r.disabled = !redo.length || readOnly;
  }

  /* ---------- hint ---------- */
  function renderHint() {
    const H = {
      select: canEdit
        ? '<b>Click</b> a wall, door, label or note to select it. <b>Drag a wall</b> to slide it (Shift to move freely). <b>Drag a corner</b> to reshape; joined walls follow. <b>Double-click a wall</b> to add a corner, <b>double-click a corner</b> to remove it. Drag empty space to pan.'
        : '<b>View only.</b> Click anything to see its details. Drag to pan, scroll or pinch to zoom.',
      wall: draft ? '<b>Click</b> to add each corner. <b>Double-click</b> or press <b>Enter</b> to finish. Hold <b>Shift</b> for straight and 45° lines. <b>Esc</b> to stop.' : '<b>Click</b> to start a new wall. It is marked <b>New</b>; change it in the panel.',
      door: '<b>Click on a wall</b> to put a door in it. It lines up with the wall.',
      label: '<b>Click</b> to place a room name.',
      note: '<b>Click</b> where the note applies. Type the note in the panel, then drag its box clear of the walls.',
      measure: '<b>Drag</b> between two points to measure. To set the scale, measure a length you know and type it in the panel.',
      pan: '<b>Drag</b> to move around. Scroll or pinch to zoom.',
    };
    const hn = el('hint'); hn.style.display = ''; hn.innerHTML = H[tool] || '';
  }
  function flash(msg) { const h = el('hint'); h.innerHTML = esc(msg); h.style.display = 'block'; clearTimeout(flashT); flashT = setTimeout(renderHint, 4500); }

  /* ---------- side panel ---------- */
  function stats() { let nl = 0, rl = 0, nc = 0, rc = 0; plan.walls.forEach(w => { if (w.status === 'new') { nl += wallLen(w); nc++; } if (w.status === 'remove') { rl += wallLen(w); rc++; } }); return { nl, rl, nc, rc }; }
  const dis = readOnly => (readOnly ? 'disabled' : '');
  function fld(id, label, val, attr = '') { return `<div class="fp-field" ${attr} style="flex:1;min-width:0"><label for="fp-${id}">${label}</label><input type="text" id="fp-${id}" data-f="${id}" value="${esc(val)}" ${dis(readOnly)}></div>`; }
  function renderSide() {
    const ae = document.activeElement;
    if (side.contains(ae) && ae.matches('input[type=text],input[type=number],textarea')) return; // don't rebuild under the cursor
    const it = findSel(); let h;
    if (tool === 'measure' && measure && measure.b && dist(measure.a, measure.b) > 0.05) h = measurePanel();
    else if (it && sel.type === 'wall') h = wallPanel(it);
    else if (it && sel.type === 'door') h = doorPanel(it);
    else if (it && sel.type === 'label') h = labelPanel(it);
    else if (it && sel.type === 'note') h = notePanel(it);
    else h = planPanel();
    side.innerHTML = h; bindSide();
  }
  function planPanel() {
    const st = stats(), m = plan.meta, sc = plan.scan;
    let h = `<div class="fp-sec"><h3>Changes so far</h3><div class="fp-stats">
      <div class="fp-stat"><span class="fp-lbl"><i class="fp-sw" style="background:${STATUS.new.col}"></i>New walls</span><b>${st.nl.toFixed(1)} m</b><span class="fp-unit">${st.nc} run${st.nc === 1 ? '' : 's'}</span></div>
      <div class="fp-stat"><span class="fp-lbl"><i class="fp-sw" style="background:${STATUS.remove.col}"></i>To remove</span><b>${st.rl.toFixed(1)} m</b><span class="fp-unit">${st.rc} run${st.rc === 1 ? '' : 's'}</span></div>
      </div>${canEdit ? '<p class="fp-tip">Select any existing wall and set it to <b>Remove</b> to show demolition. New walls you draw show in green with their lengths.</p>' : ''}</div>`;
    h += '<div class="fp-sec"><h3>Notes on the drawing</h3><div class="fp-notes">';
    if (!plan.notes.length) h += `<p class="fp-empty">No notes yet.${canEdit ? ' Pick the note tool (N) and click on the drawing.' : ''}</p>`;
    plan.notes.forEach((n, i) => { const key = NOTE_COL[n.color] ? n.color : 'amber'; h += `<button class="fp-note-item" data-note="${n.id}"><span class="fp-pin" style="background:${NOTE_COL[key]};color:${NOTE_INK[key]}">${i + 1}</span><span>${esc(n.text || 'Empty note')}</span></button>`; });
    h += '</div></div>';
    h += `<div class="fp-sec"><h3>View</h3>
      ${sc && scanHref ? `<label class="fp-check"><input type="checkbox" data-o="scan" ${sc.show !== false ? 'checked' : ''}> Show original drawing underneath</label>
      <div class="fp-field"><label for="fp-op">Drawing strength</label><input type="range" id="fp-op" min="0.05" max="1" step="0.05" value="${sc.opacity}"></div>
      <label class="fp-check"><input type="checkbox" data-o="invert" ${opt.invert ? 'checked' : ''}> Invert drawing</label>` : '<p class="fp-empty">No original drawing attached.</p>'}
      <label class="fp-check"><input type="checkbox" data-o="dims" ${opt.dims ? 'checked' : ''}> Show every wall length</label>
      <label class="fp-check"><input type="checkbox" data-o="grid" ${opt.grid ? 'checked' : ''}> Grid (0.5 m and 5 m)</label>
      ${canEdit ? `<label class="fp-check"><input type="checkbox" data-o="snap" ${opt.snap ? 'checked' : ''}> Snap to corners and 50 mm</label>` : ''}</div>`;
    h += `<div class="fp-sec"><h3>Title block</h3>
      ${fld('project', 'Project', m.project)}${fld('client', 'Client', m.client)}${fld('site', 'Site', m.site)}
      <div class="fp-row fp-nowrap">${fld('drawing', 'Drawing', m.drawing)}${fld('rev', 'Rev', m.rev, 'style="max-width:70px"')}</div>
      <div class="fp-row fp-nowrap">${fld('drawnBy', 'Drawn by', m.drawnBy)}${fld('date', 'Date', m.date, 'style="max-width:110px"')}</div>
      <div class="fp-field"><label for="fp-note">Drawing note</label><textarea id="fp-note" data-f="note" style="min-height:60px" ${dis(readOnly)}>${esc(m.note)}</textarea></div></div>`;
    if (canEdit) h += '<div class="fp-sec"><p class="fp-tip">To set the scale, pick the measure tool, drag along a length you know (a wall you have measured on site, or a dimension written on the drawing) and type its true length.</p></div>';
    return h;
  }
  function measurePanel() {
    const L = dist(measure.a, measure.b);
    return `<div class="fp-sec"><h3>Measure</h3><h2>${L.toFixed(2)} m on the drawing</h2>
      ${canEdit ? `<div class="fp-field"><label for="fp-true">True length (m)</label><div class="fp-row fp-nowrap"><input type="number" id="fp-true" step="0.01" min="0.05" placeholder="e.g. 6.58"><button class="fp-btn fp-primary" data-act2="rescale">Set scale</button></div></div>
      <p class="fp-tip">Everything on the plan and the original drawing scale to match. Undo puts it back.</p>` : ''}</div>`;
  }
  function wallPanel(w) {
    const mm = Math.round(w.t * 1000);
    let h = `<div class="fp-sec"><h3>Wall</h3><h2>${STATUS[w.status].name} wall, ${wallLen(w).toFixed(2)} m</h2>
    <div class="fp-seg" role="group" aria-label="Status">${Object.entries(STATUS).map(([k, v]) => `<button data-status="${k}" aria-pressed="${w.status === k}" ${dis(readOnly)}><i class="fp-sw" style="background:${v.col}"></i>${v.name}</button>`).join('')}</div></div>`;
    h += `<div class="fp-sec"><h3>Thickness</h3><div class="fp-seg" role="group"><button data-th="100" aria-pressed="${mm === 100}" ${dis(readOnly)}>100 mm stud</button><button data-th="120" aria-pressed="${mm === 120}" ${dis(readOnly)}>120 mm</button><button data-th="300" aria-pressed="${mm === 300}" ${dis(readOnly)}>300 mm ext</button></div>
    <div class="fp-row fp-nowrap"><input type="number" id="fp-wt" min="20" max="800" step="10" value="${mm}" ${dis(readOnly)}><span class="fp-unit">mm</span></div></div>`;
    h += `<div class="fp-sec"><h3>Lengths</h3>${canEdit ? `<label class="fp-check"><input type="checkbox" data-o="calib" ${opt.calib ? 'checked' : ''}> Rescale whole plan from a length I measured</label>` : ''}<div class="fp-segs">`;
    for (let i = 1; i < w.pts.length; i++) h += `<div class="fp-segrow"><span>${i}</span><input type="number" step="0.01" min="0.05" data-len="${i}" value="${dist(w.pts[i - 1], w.pts[i]).toFixed(2)}" aria-label="Length of section ${i} in metres" ${dis(readOnly)}><span class="fp-unit">m</span></div>`;
    h += '</div>';
    if (canEdit) h += `<p class="fp-tip">${opt.calib ? 'Type the true length of one section. Every wall, door, label, note and the original drawing scale to match.' : 'Typing a length moves the far end of that section; the rest of the wall follows.'}</p>`;
    h += '</div>';
    if (canEdit) h += `<div class="fp-sec"><div class="fp-row"><button class="fp-btn" data-act2="dup">Duplicate</button><button class="fp-btn fp-danger" data-act2="del">Delete wall</button></div>
    <p class="fp-tip"><b>Drag the wall</b> to slide that section square to itself; hold <b>Shift</b> to move it freely. <b>Drag a corner</b> to move it. <b>Double-click the wall</b> to add a corner. <b>Double-click a corner</b> to remove it.</p></div>`;
    return h;
  }
  function doorPanel(d) {
    const mm = Math.round(d.w * 1000);
    return `<div class="fp-sec"><h3>Door</h3><h2>${d.style === 'opening' ? 'Opening' : 'Door'}, ${mm} mm</h2>
    <div class="fp-seg"><button data-dstyle="door" aria-pressed="${d.style !== 'opening'}" ${dis(readOnly)}>Door</button><button data-dstyle="opening" aria-pressed="${d.style === 'opening'}" ${dis(readOnly)}>Opening</button></div></div>
    <div class="fp-sec"><h3>Width</h3><div class="fp-seg">${[762, 838, 926, 1600].map(v => `<button data-dw="${v / 1000}" aria-pressed="${mm === v}" ${dis(readOnly)}>${v}</button>`).join('')}</div>
    <div class="fp-row fp-nowrap"><input type="number" id="fp-dw" min="300" max="4000" step="10" value="${mm}" ${dis(readOnly)}><span class="fp-unit">mm</span></div></div>
    ${canEdit ? `<div class="fp-sec"><h3>Swing</h3><div class="fp-row"><button class="fp-btn" data-act2="flip">Swing other side</button><button class="fp-btn" data-act2="hand">Hinge other end</button></div>
    <div class="fp-row fp-nowrap"><span class="fp-lbl" style="flex:none">Angle</span><input type="number" id="fp-da" step="1" value="${Math.round(d.a)}"><span class="fp-unit">°</span></div></div>
    <div class="fp-sec"><div class="fp-row"><button class="fp-btn fp-danger" data-act2="del">Delete door</button></div><p class="fp-tip">Drag the door along its wall to move it.</p></div>` : ''}`;
  }
  function labelPanel(l) {
    return `<div class="fp-sec"><h3>Room label</h3>${fld('ltext', 'Name', l.text)}${fld('lsub', 'Second line (area, use)', l.sub)}${canEdit ? '<div class="fp-row"><button class="fp-btn fp-danger" data-act2="del">Delete label</button></div>' : ''}</div>`;
  }
  function notePanel(n) {
    const i = plan.notes.indexOf(n), key = NOTE_COL[n.color] ? n.color : 'amber';
    return `<div class="fp-sec"><h3>Note ${i + 1}</h3><div class="fp-field"><label for="fp-ntext">Note</label><textarea id="fp-ntext" placeholder="What needs doing here" ${dis(readOnly)}>${esc(n.text)}</textarea></div>
    ${canEdit ? `<div class="fp-field"><span class="fp-lbl">Colour</span><div class="fp-seg">${Object.keys(NOTE_COL).map(c => `<button data-ncol="${c}" aria-pressed="${key === c}"><i class="fp-sw" style="background:${NOTE_COL[c]}"></i>${NOTE_NAME[c]}</button>`).join('')}</div></div>
    <label class="fp-check"><input type="checkbox" id="fp-nhide" ${n.hide ? 'checked' : ''}> Show only the number on the drawing</label>
    <div class="fp-row"><button class="fp-btn fp-danger" data-act2="del">Delete note</button><button class="fp-btn fp-ghost" data-act2="done">Done</button></div>
    <p class="fp-tip">Drag the numbered pin to the spot it refers to, and drag the text box somewhere clear.</p>` : ''}</div>`;
  }
  function bindSide() {
    const it = findSel();
    const q = s => side.querySelector(s), qa = s => side.querySelectorAll(s);
    const on = (s, ev, fn) => { const e = q(s); if (e) e[ev] = fn; };
    const a2 = (n, fn) => on(`[data-act2="${n}"]`, 'onclick', fn);
    qa('[data-note]').forEach(b => { b.onclick = () => { const n = byId(plan.notes, b.dataset.note); sel = { type: 'note', id: n.id }; centreOn([n.x, n.y]); render(); }; });
    qa('[data-o]').forEach(c => {
      c.onchange = () => {
        const k = c.dataset.o;
        if (k === 'scan') { mutate(() => { plan.scan.show = c.checked; }); return; }
        opt[k] = c.checked; store('cw-fp-opts', JSON.stringify(opt)); render();
      };
    });
    on('#fp-op', 'oninput', e => { plan.scan.opacity = +e.target.value; const img = world.querySelector('image'); if (img) img.setAttribute('opacity', e.target.value); });
    on('#fp-op', 'onchange', () => { if (!readOnly) changed(); });
    ['project', 'client', 'site', 'drawing', 'rev', 'drawnBy', 'date', 'note'].forEach(k => on(`[data-f="${k}"]`, 'onchange', e => mutate(() => { plan.meta[k] = e.target.value; titleBar(); })));
    a2('rescale', () => { const v = +q('#fp-true').value, cur = dist(measure.a, measure.b); if (!(v > 0) || cur < 1e-6) { flash('Type the true length in metres first.'); return; } const f = v / cur; mutate(() => scaleAll(f)); measure = { a: [r3(measure.a[0] * f), r3(measure.a[1] * f)], b: [r3(measure.b[0] * f), r3(measure.b[1] * f)] }; render(); flash(`Scale set. That length now reads ${v.toFixed(2)} m.`); });
    on('#fp-true', 'onkeydown', e => { if (e.key === 'Enter') { e.target.blur(); const b = q('[data-act2="rescale"]'); if (b) b.click(); } });
    if (!it) return;
    qa('[data-status]').forEach(b => { b.onclick = () => mutate(() => { it.status = b.dataset.status; }); });
    qa('[data-th]').forEach(b => { b.onclick = () => mutate(() => { it.t = +b.dataset.th / 1000; }); });
    on('#fp-wt', 'onchange', e => { const v = +e.target.value; if (v >= 20) mutate(() => { it.t = v / 1000; }); });
    qa('[data-len]').forEach(inp => { inp.onchange = () => { const i = +inp.dataset.len, v = +inp.value; if (!(v > 0)) return; inp.blur(); setLength(it, i, v); }; });
    a2('dup', () => mutate(() => { const c = JSON.parse(JSON.stringify(it)); c.id = uid('w'); c.pts = c.pts.map(p => [r3(p[0] + 0.5), r3(p[1] + 0.5)]); plan.walls.push(c); sel = { type: 'wall', id: c.id }; }));
    a2('del', delSel);
    qa('[data-dstyle]').forEach(b => { b.onclick = () => mutate(() => { it.style = b.dataset.dstyle; }); });
    qa('[data-dw]').forEach(b => { b.onclick = () => mutate(() => { it.w = +b.dataset.dw; }); });
    on('#fp-dw', 'onchange', e => { const v = +e.target.value; if (v >= 300) mutate(() => { it.w = v / 1000; }); });
    a2('flip', () => mutate(() => { it.flip = !it.flip; }));
    a2('hand', () => mutate(() => { it.hx = -(it.hx || 1); }));
    on('#fp-da', 'onchange', e => mutate(() => { it.a = +e.target.value || 0; }));
    on('[data-f="ltext"]', 'onchange', e => mutate(() => { it.text = e.target.value || 'Room'; }));
    on('[data-f="lsub"]', 'onchange', e => mutate(() => { it.sub = e.target.value; }));
    on('#fp-ntext', 'onfocus', () => { liveBefore = snapshot(); });
    on('#fp-ntext', 'oninput', e => { if (liveBefore == null) liveBefore = snapshot(); it.text = e.target.value; render(); });
    on('#fp-ntext', 'onchange', () => { if (liveBefore != null) { const before = liveBefore; liveBefore = null; if (before !== snapshot()) { pushUndo(before); changed(); } } });
    qa('[data-ncol]').forEach(b => { b.onclick = () => mutate(() => { it.color = b.dataset.ncol; }); });
    on('#fp-nhide', 'onchange', e => mutate(() => { it.hide = e.target.checked; }));
    a2('done', () => { sel = null; render(); });
  }
  function setLength(w, i, v) {
    if (opt.calib) { const cur = dist(w.pts[i - 1], w.pts[i]); if (cur < 1e-6) return; const f = v / cur; mutate(() => scaleAll(f)); return; }
    mutate(() => {
      const a = w.pts[i - 1], b = w.pts[i], L = dist(a, b) || 1;
      const nb = [r3(a[0] + (b[0] - a[0]) / L * v), r3(a[1] + (b[1] - a[1]) / L * v)], dx = nb[0] - b[0], dy = nb[1] - b[1];
      for (let j = i; j < w.pts.length; j++) w.pts[j] = [r3(w.pts[j][0] + dx), r3(w.pts[j][1] + dy)];
    });
  }
  function scaleAll(f) {
    const S = p => [r3(p[0] * f), r3(p[1] * f)];
    plan.walls.forEach(w => { w.pts = w.pts.map(S); });
    plan.doors.forEach(d => { [d.x, d.y] = S([d.x, d.y]); });
    plan.labels.forEach(l => { [l.x, l.y] = S([l.x, l.y]); });
    plan.notes.forEach(n => { [n.x, n.y] = S([n.x, n.y]); [n.tx, n.ty] = S([n.tx, n.ty]); });
    const s = plan.scan;
    if (s) { s.x = r3(s.x * f); s.y = r3(s.y * f); s.w = r3(s.w * f); s.h = r3(s.h * f); }
    view.k /= f;
  }
  function delSel() { const m = { wall: 'walls', door: 'doors', label: 'labels', note: 'notes' }[sel && sel.type]; if (!m) return; mutate(() => { plan[m] = plan[m].filter(o => o.id !== sel.id); sel = null; }); }

  /* ---------- pointer interaction ---------- */
  function setTool(t) {
    if (draft && t !== 'wall') finishDraft();
    tool = t; measure = null;
    root.querySelectorAll('.fp-tool').forEach(b => b.setAttribute('aria-pressed', b.dataset.tool === t));
    svg.setAttribute('class', 'fp-svg' + (t === 'pan' ? ' c-grab' : t === 'select' ? '' : ' c-cross'));
    render();
  }
  function targetOf(target) { const e = target && target.closest ? target.closest('[data-k]') : null; return e ? { k: e.dataset.k, id: e.dataset.id, i: e.dataset.i != null ? +e.dataset.i : null } : null; }
  const moved4 = (dx, dy) => Math.hypot(dx, dy) * view.k >= 4;

  function onDown(e) {
    if (side.contains(document.activeElement)) document.activeElement.blur();
    svg.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, [e.clientX, e.clientY]);
    if (pointers.size === 2) { startPinch(); return; }
    if (pointers.size > 2) return;
    const p = toWorld(e);
    if (e.button === 1 || tool === 'pan' || spaceDown) { startPan(e); return; }
    if (e.button === 2) return;
    const tg = targetOf(e.target);
    if (tool === 'select') {
      if (!tg) { sel = null; render(); startPan(e); return; }
      const before = snapshot();
      if (tg.k === 'v') {
        const w = byId(plan.walls, tg.id); sel = { type: 'wall', id: tg.id, vi: tg.i };
        drag = { kind: 'vertex', id: tg.id, i: tg.i, links: linked([w.pts[tg.i]], w, [tg.i]).filter(l => !(l.w === w && l.i === tg.i)), before, moved: false };
      } else if (tg.k === 'wall') {
        const w = byId(plan.walls, tg.id); let si = 1, bd = 1e9;
        for (let i = 1; i < w.pts.length; i++) { const r = projSeg(p, w.pts[i - 1], w.pts[i]); if (r.d < bd) { bd = r.d; si = i; } }
        sel = { type: 'wall', id: tg.id, si };
        const a = w.pts[si - 1], c = w.pts[si], L = dist(a, c) || 1;
        drag = { kind: 'seg', id: tg.id, start: p, n: [-(c[1] - a[1]) / L, (c[0] - a[0]) / L], links: linked([a, c], w, [si - 1, si]), before, moved: false };
      } else if (tg.k === 'door') { const d = byId(plan.doors, tg.id); sel = { type: 'door', id: tg.id }; drag = { kind: 'door', id: tg.id, start: p, orig: [d.x, d.y], before, moved: false }; }
      else if (tg.k === 'label') { const l = byId(plan.labels, tg.id); sel = { type: 'label', id: tg.id }; drag = { kind: 'label', id: tg.id, start: p, orig: [l.x, l.y], before, moved: false }; }
      else if (tg.k === 'pin') { const n = byId(plan.notes, tg.id); sel = { type: 'note', id: tg.id }; drag = { kind: 'pin', id: tg.id, start: p, orig: [n.x, n.y], before, moved: false }; }
      else if (tg.k === 'nbox') { const n = byId(plan.notes, tg.id); sel = { type: 'note', id: tg.id }; drag = { kind: 'nbox', id: tg.id, start: p, orig: [n.tx, n.ty], before, moved: false }; }
      if (readOnly && drag && drag.kind !== 'pan') drag = null;
      openSheet(); render(); return;
    }
    if (tool === 'measure') { const sp = snapPt(p); measure = { a: sp, b: sp }; drag = { kind: 'measure' }; render(); return; }
    if (readOnly) return;
    if (tool === 'wall') {
      const sp = snapPt(p, { from: draft ? draft.pts[draft.pts.length - 1] : null, shift: e.shiftKey });
      if (!draft) draft = { pts: [sp], cur: sp, t: 0.1 };
      else {
        const last = draft.pts[draft.pts.length - 1];
        if (dist(last, sp) < 0.02) { finishDraft(); return; }
        if (draft.pts.length > 2 && dist(draft.pts[0], sp) < 0.02) { draft.pts.push(sp); finishDraft(); return; }
        draft.pts.push(sp);
      }
      render(); return;
    }
    if (tool === 'door') {
      const nw = nearestWall(p), onWall = nw.d < 0.8;
      mutate(() => { const d = { id: uid('d'), x: r3(onWall ? nw.q[0] : p[0]), y: r3(onWall ? nw.q[1] : p[1]), a: onWall ? Math.round(nw.a * 10) / 10 : 0, w: 0.838, flip: false, hx: 1, style: 'door', t: r3((onWall ? nw.t : 0.12) + 0.06) }; plan.doors.push(d); sel = { type: 'door', id: d.id }; });
      setTool('select'); openSheet(); return;
    }
    if (tool === 'label') { mutate(() => { const l = { id: uid('l'), x: r3(p[0]), y: r3(p[1]), text: 'New room', sub: '' }; plan.labels.push(l); sel = { type: 'label', id: l.id }; }); setTool('select'); openSheet(); focusLater('[data-f="ltext"]'); return; }
    if (tool === 'note') { mutate(() => { const n = { id: uid('n'), x: r3(p[0]), y: r3(p[1]), tx: r3(p[0] + 0.8), ty: r3(p[1] - 1.6), text: '', color: 'amber' }; plan.notes.push(n); sel = { type: 'note', id: n.id }; }); setTool('select'); openSheet(); focusLater('#fp-ntext'); }
  }
  function onMove(e) {
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, [e.clientX, e.clientY]);
    if (pinch) { doPinch(); return; }
    const p = toWorld(e);
    if (drag) {
      if (drag.kind === 'pan') { view.tx = drag.tx + (e.clientX - drag.sx); view.ty = drag.ty + (e.clientY - drag.sy); render(); return; }
      if (drag.kind === 'measure') { measure.b = snapPt(p, { from: measure.a, shift: e.shiftKey }); render(); return; }
      const dx = p[0] - (drag.start ? drag.start[0] : 0), dy = p[1] - (drag.start ? drag.start[1] : 0);
      if (drag.kind === 'vertex') {
        const w = byId(plan.walls, drag.id), nb = w.pts[drag.i - 1] || w.pts[drag.i + 1];
        const np = snapPt(p, { exclude: { id: w.id, i: drag.i }, from: e.shiftKey ? nb : null, shift: e.shiftKey, skip: drag.links });
        w.pts[drag.i] = np; drag.links.forEach(l => { l.w.pts[l.i] = [np[0], np[1]]; }); drag.moved = true;
      } else if (drag.kind === 'seg') {
        if (!drag.moved && !moved4(dx, dy)) return;
        const g = opt.snap ? 0.05 : 0.001; let mx, my;
        if (e.shiftKey) { mx = Math.round(dx / g) * g; my = Math.round(dy / g) * g; }
        else { const t = Math.round((dx * drag.n[0] + dy * drag.n[1]) / g) * g; mx = drag.n[0] * t; my = drag.n[1] * t; }
        drag.links.forEach(l => { l.w.pts[l.i] = [r3(l.o[0] + mx), r3(l.o[1] + my)]; }); drag.moved = true;
      } else if (drag.kind === 'door') {
        if (!drag.moved && !moved4(dx, dy)) return;
        const d = byId(plan.doors, drag.id), target = [drag.orig[0] + dx, drag.orig[1] + dy], nw = nearestWall(target);
        if (nw.d < 0.4 && !e.altKey) { d.x = r3(nw.q[0]); d.y = r3(nw.q[1]); d.a = Math.round(nw.a * 10) / 10; } else { d.x = r3(target[0]); d.y = r3(target[1]); }
        drag.moved = true;
      } else {
        if (!drag.moved && !moved4(dx, dy)) return;
        const o = drag.kind === 'label' ? byId(plan.labels, drag.id) : byId(plan.notes, drag.id);
        const nx = r3(drag.orig[0] + dx), ny = r3(drag.orig[1] + dy);
        if (drag.kind === 'nbox') { o.tx = nx; o.ty = ny; } else { o.x = nx; o.y = ny; }
        drag.moved = true;
      }
      render(); return;
    }
    if (tool === 'wall') { const sp = snapPt(p, { from: draft ? draft.pts[draft.pts.length - 1] : null, shift: e.shiftKey }); if (draft) draft.cur = sp; hover = sp; render(); }
  }
  function onUp(e) {
    pointers.delete(e.pointerId);
    if (pinch) { if (pointers.size < 2) pinch = null; return; }
    if (!drag) return;
    const d = drag; drag = null;
    if (d.kind === 'pan') { svg.classList.remove('c-grabbing'); return; }
    if (d.kind === 'measure') { render(); return; }
    if (d.moved && d.before !== snapshot()) { pushUndo(d.before); changed(); } else render();
  }
  function onDbl(e) {
    if (tool === 'wall') { if (draft) { if (draft.pts.length > 1 && dist(draft.pts[draft.pts.length - 1], draft.pts[draft.pts.length - 2]) < 0.02) draft.pts.pop(); finishDraft(); } return; }
    if (tool !== 'select' || readOnly) return;
    const tg = targetOf(document.elementFromPoint(e.clientX, e.clientY)), p = toWorld(e);
    if (!tg) return;
    if (tg.k === 'v') { const w = byId(plan.walls, tg.id); if (w.pts.length > 2) mutate(() => { w.pts.splice(tg.i, 1); sel = { type: 'wall', id: w.id }; }); return; }
    if (tg.k === 'wall') {
      const w = byId(plan.walls, tg.id); let bi = 1, bd = 1e9, bq = null;
      for (let i = 1; i < w.pts.length; i++) { const r = projSeg(p, w.pts[i - 1], w.pts[i]); if (r.d < bd) { bd = r.d; bi = i; bq = r.q; } }
      mutate(() => { w.pts.splice(bi, 0, [r3(bq[0]), r3(bq[1])]); sel = { type: 'wall', id: w.id, vi: bi }; });
      return;
    }
    if (tg.k === 'label' || tg.k === 'pin' || tg.k === 'nbox') { openSheet(); focusLater(tg.k === 'label' ? '[data-f="ltext"]' : '#fp-ntext'); }
  }
  function onWheel(e) { e.preventDefault(); const r = svg.getBoundingClientRect(); zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015))); }
  function zoomAt(mx, my, f) { userView = true; const k2 = Math.max(4, Math.min(400, view.k * f)); f = k2 / view.k; view.tx = mx - (mx - view.tx) * f; view.ty = my - (my - view.ty) * f; view.k = k2; render(); }
  function startPan(e) { userView = true; drag = { kind: 'pan', sx: e.clientX, sy: e.clientY, tx: view.tx, ty: view.ty }; svg.classList.add('c-grabbing'); }
  function startPinch() { drag = null; const [a, b] = [...pointers.values()], r = svg.getBoundingClientRect(); pinch = { d: Math.hypot(a[0] - b[0], a[1] - b[1]), cx: (a[0] + b[0]) / 2 - r.left, cy: (a[1] + b[1]) / 2 - r.top, k: view.k, tx: view.tx, ty: view.ty }; }
  function doPinch() {
    userView = true;
    const [a, b] = [...pointers.values()]; if (!a || !b) return;
    const r = svg.getBoundingClientRect(), d = Math.hypot(a[0] - b[0], a[1] - b[1]), cx = (a[0] + b[0]) / 2 - r.left, cy = (a[1] + b[1]) / 2 - r.top;
    const k2 = Math.max(4, Math.min(400, pinch.k * d / pinch.d)), f = k2 / pinch.k;
    view.k = k2; view.tx = cx - (pinch.cx - pinch.tx) * f; view.ty = cy - (pinch.cy - pinch.ty) * f; render();
  }
  function finishDraft() {
    const d = draft; draft = null; hover = null;
    if (d && d.pts.length >= 2) { mutate(() => { const w = { id: uid('w'), pts: d.pts, t: d.t, status: 'new', kind: 'int' }; plan.walls.push(w); sel = { type: 'wall', id: w.id }; }); setTool('select'); openSheet(); }
    else render();
  }
  function focusLater(s) { setTimeout(() => { const e = side.querySelector(s); if (e) { e.focus(); if (e.select) e.select(); } }, 30); }
  function bbox() {
    let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    const add = p => { x0 = Math.min(x0, p[0]); y0 = Math.min(y0, p[1]); x1 = Math.max(x1, p[0]); y1 = Math.max(y1, p[1]); };
    plan.walls.forEach(w => w.pts.forEach(add));
    plan.notes.forEach(n => { add([n.x, n.y]); if (!n.hide) { const b = noteBox(n); add([n.tx, n.ty]); add([n.tx + b.w, n.ty + b.h]); } });
    if (x0 > x1 && plan.scan) { add([plan.scan.x, plan.scan.y]); add([plan.scan.x + plan.scan.w, plan.scan.y + plan.scan.h]); }
    if (x0 > x1) { x0 = 0; y0 = 0; x1 = 20; y1 = 15; }
    return { x0, y0, x1, y1 };
  }
  function fit() {
    const r = svg.getBoundingClientRect(); if (!r.width) return;
    const b = bbox(), pad = 56;
    const k = Math.min((r.width - pad * 2) / (b.x1 - b.x0 || 1), (r.height - pad * 2) / (b.y1 - b.y0 || 1));
    view.k = Math.max(4, Math.min(400, k));
    view.tx = (r.width - (b.x1 - b.x0) * view.k) / 2 - b.x0 * view.k;
    view.ty = (r.height - (b.y1 - b.y0) * view.k) / 2 - b.y0 * view.k;
    render();
  }
  function centreOn(p) { const r = svg.getBoundingClientRect(); view.tx = r.width / 2 - p[0] * view.k; view.ty = r.height / 2 - p[1] * view.k; }

  /* ---------- keyboard ---------- */
  function onKey(e) {
    const typing = e.target.matches && e.target.matches('input,textarea,select');
    const mod = e.ctrlKey || e.metaKey, key = e.key.toLowerCase();
    if (mod && key === 's') { e.preventDefault(); save(); return; }
    if (typing) return;
    if (mod && key === 'z') { e.preventDefault(); if (readOnly) return; if (e.shiftKey) doRedo(); else doUndo(); return; }
    if (mod && key === 'y') { e.preventDefault(); if (!readOnly) doRedo(); return; }
    if (e.key === ' ') { spaceDown = true; svg.classList.add('c-grab'); e.preventDefault(); return; }
    if (e.key === 'Escape') { if (draft) { finishDraft(); return; } measure = null; sel = null; setTool('select'); return; }
    if (e.key === 'Enter' && draft) { finishDraft(); return; }
    if ((e.key === 'Delete' || e.key === 'Backspace') && sel && !readOnly) {
      e.preventDefault();
      if (sel.type === 'wall' && sel.vi != null) { const w = findSel(); if (w && w.pts.length > 2) { mutate(() => { w.pts.splice(sel.vi, 1); sel.vi = null; }); return; } }
      delSel(); return;
    }
    const map = canEdit ? { v: 'select', w: 'wall', d: 'door', t: 'label', n: 'note', m: 'measure', h: 'pan' } : { v: 'select', m: 'measure', h: 'pan' };
    if (!mod && !e.altKey && map[key]) { setTool(map[key]); return; }
    if (key === 'f') { fit(); return; }
    const r = svg.getBoundingClientRect();
    if (e.key === '+' || e.key === '=') zoomAt(r.width / 2, r.height / 2, 1.25);
    if (e.key === '-') zoomAt(r.width / 2, r.height / 2, 0.8);
    if (sel && e.key.startsWith('Arrow') && !readOnly) {
      e.preventDefault();
      const st = e.shiftKey ? 0.1 : 0.01;
      nudge(e.key === 'ArrowLeft' ? -st : e.key === 'ArrowRight' ? st : 0, e.key === 'ArrowUp' ? -st : e.key === 'ArrowDown' ? st : 0);
    }
  }
  function onKeyUp(e) { if (e.key === ' ') { spaceDown = false; if (tool !== 'pan') svg.classList.remove('c-grab'); } }
  function nudge(dx, dy) {
    const it = findSel(); if (!it) return;
    mutate(() => {
      if (sel.type === 'wall') { if (sel.vi != null) { const p = it.pts[sel.vi]; it.pts[sel.vi] = [r3(p[0] + dx), r3(p[1] + dy)]; } else it.pts = it.pts.map(p => [r3(p[0] + dx), r3(p[1] + dy)]); }
      else { it.x = r3(it.x + dx); it.y = r3(it.y + dy); if (sel.type === 'note') { it.tx = r3(it.tx + dx); it.ty = r3(it.ty + dy); } }
    });
  }

  /* ---------- top bar ---------- */
  function titleBar() { const m = plan.meta; el('title').textContent = m.project || 'Untitled plan'; el('sub').textContent = [m.client, `${m.drawing || 'Drawing'}, Rev ${m.rev || '-'}`].filter(Boolean).join(' · '); }
  function setStatus(txt) {
    const s = el('status');
    if (txt) { s.textContent = txt; s.className = 'fp-status'; return; }
    s.className = 'fp-status' + (dirty && !readOnly ? ' dirty' : '');
    s.textContent = !canEdit ? 'View only' : readOnly ? 'Not saving' : dirty ? 'Unsaved changes' : lastSaved ? 'Saved ' + lastSaved : 'Up to date';
  }
  function showBanner(text, aLabel, aFn) {
    el('bannerText').textContent = text; const a = el('bannerA');
    a.hidden = !aLabel; a.textContent = aLabel || ''; a.onclick = () => { el('banner').hidden = true; if (aFn) aFn(); };
    el('banner').hidden = false;
  }
  function openSheet() { if (window.matchMedia('(max-width:900px)').matches && sel) { side.classList.add('open'); act('sheet').setAttribute('aria-expanded', 'true'); } }

  root.querySelectorAll('.fp-tool').forEach(b => { b.onclick = () => setTool(b.dataset.tool); });
  act('back').onclick = () => opts.onBack && opts.onBack();
  act('undo').onclick = doUndo; act('redo').onclick = doRedo;
  act('zin').onclick = () => { const r = svg.getBoundingClientRect(); zoomAt(r.width / 2, r.height / 2, 1.25); };
  act('zout').onclick = () => { const r = svg.getBoundingClientRect(); zoomAt(r.width / 2, r.height / 2, 0.8); };
  act('zfit').onclick = fit;
  act('sheet').onclick = () => { const o = side.classList.toggle('open'); act('sheet').setAttribute('aria-expanded', o); };
  if (act('save')) act('save').onclick = () => save();
  act('modalClose').onclick = () => { el('modal').hidden = true; };
  el('bannerB').onclick = () => { el('banner').hidden = true; };
  const exp = act('export'), mexp = el('mexp');
  exp.onclick = e => { e.stopPropagation(); mexp.hidden = !mexp.hidden; exp.setAttribute('aria-expanded', !mexp.hidden); };
  const onDocClick = e => { if (!mexp.hidden && !mexp.contains(e.target) && e.target !== exp) { mexp.hidden = true; exp.setAttribute('aria-expanded', 'false'); } };
  document.addEventListener('click', onDocClick);
  mexp.querySelectorAll('button').forEach(b => {
    b.onclick = () => {
      mexp.hidden = true; const x = b.dataset.x;
      if (x === 'svg') offer(fname('svg'), new Blob([buildSVG()], { type: 'image/svg+xml' }));
      if (x === 'png') exportPNG();
      if (x === 'print') printA3('fill');
      if (x === 'print-std') printA3('standard');
      if (x === 'json') offer(fname('json').replace('.json', '-backup.json'), new Blob([JSON.stringify(plan, null, 1)], { type: 'application/json' }));
      if (x === 'import') el('fimport').click();
    };
  });
  el('fimport').onchange = e => {
    const f = e.target.files[0]; if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      try {
        const p = JSON.parse(rd.result); if (!Array.isArray(p.walls)) throw new Error('no walls');
        delete p.scanData;
        mutate(() => { const keepScan = plan.scan; plan = normalisePlan(p); if (!plan.scan || !scanHref) plan.scan = keepScan; sel = null; });
        fit(); flash(`Drawing restored from ${f.name}. Save to keep it.`);
      } catch (err) { flash('That file is not a floor plan backup. Choose a .json file exported from a floor plan.'); }
    };
    rd.readAsText(f); e.target.value = '';
  };

  /* ---------- saving ---------- */
  function scheduleAutosave() { clearTimeout(autoT); if (readOnly) return; autoT = setTimeout(() => { if (dirty && !drag && !draft) save(true); }, 12000); }
  async function save(auto) {
    if (saving || readOnly || !opts.onSave) return;
    if (!dirty) { if (!auto) flash('Nothing new to save.'); return; }
    saving = true; setStatus('Saving…');
    const sent = snapshot();
    try {
      const res = await opts.onSave(JSON.parse(sent), baseStamp);
      baseStamp = res && res.stamp ? res.stamp : baseStamp;
      if (snapshot() === sent) { dirty = false; if (opts.draftKey) unstore(opts.draftKey); }
      else if (opts.draftKey) store(opts.draftKey, JSON.stringify({ base: baseStamp, plan }));
      lastSaved = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
      setStatus();
    } catch (err) {
      const st = err && err.status;
      if (st === 409) {
        readOnly = true; setStatus();
        showBanner('Someone else saved this plan after you opened it, so your changes have not been saved. Export a backup of your version, then reload to see theirs.', 'Export my version', () => offer(fname('json').replace('.json', '-my-version.json'), new Blob([JSON.stringify(plan, null, 1)], { type: 'application/json' })));
      } else if (st === 403) { readOnly = true; setStatus(); flash('Only admins can change floor plans. Your changes have not been saved.'); }
      else { setStatus(); if (!auto) flash('Save did not go through. Check your connection and try again; your changes are still here.'); else scheduleAutosave(); }
    }
    saving = false;
  }
  const onBeforeUnload = e => { if (dirty && !readOnly) { e.preventDefault(); e.returnValue = ''; } };
  window.addEventListener('beforeunload', onBeforeUnload);

  /* ---------- export ---------- */
  function sheetSize() { const b = bbox(), pad = 1; return { w: (b.x1 - b.x0) + pad * 2, h: (b.y1 - b.y0) + pad * 2 + 2.5 }; }
  // aspect (width / height) widens or deepens the drawing area so the sheet fills a page of that shape
  function buildSVG(scaleText, sizeAttrs, aspect) {
    const b = bbox(), P = PRINT_PAL, m = plan.meta, pad = 1;
    let x0 = b.x0 - pad, y0 = b.y0 - pad, w = (b.x1 - b.x0) + pad * 2, hPlan = (b.y1 - b.y0) + pad * 2;
    if (aspect) {
      const h0 = hPlan + 2.5;
      if (w / h0 < aspect) { const nw = h0 * aspect; x0 -= (nw - w) / 2; w = nw; }
      else { const extra = w / aspect - h0; y0 -= extra / 2; hPlan += extra; }
    }
    const h = hPlan + 2.5;
    let s = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${r3(x0)} ${r3(y0)} ${r3(w)} ${r3(h)}" ${sizeAttrs || `width="${Math.round(w * 50)}" height="${Math.round(h * 50)}"`}><rect x="${x0}" y="${y0}" width="${w}" height="${h}" fill="#FFFFFF"/>`;
    const order = { remove: 0, existing: 1, new: 2 };
    [...plan.walls].sort((a, c) => order[a.status] - order[c.status]).forEach(wl => { s += wallSVG(wl, P, false); });
    plan.doors.forEach(d => { s += doorSVG(d, P, false); });
    plan.labels.forEach(l => { s += labelSVG(l, P, false); });
    plan.walls.filter(wl => wl.status === 'new').forEach(wl => {
      for (let i = 1; i < wl.pts.length; i++) {
        const a = wl.pts[i - 1], c = wl.pts[i], L = dist(a, c); if (L < 0.15) continue;
        let ang = Math.atan2(c[1] - a[1], c[0] - a[0]) * 180 / Math.PI; if (ang > 90) ang -= 180; if (ang < -90) ang += 180;
        const nx = -(c[1] - a[1]) / L, ny = (c[0] - a[0]) / L, off = wl.t / 2 + 0.2, mx = (a[0] + c[0]) / 2 + nx * off, my = (a[1] + c[1]) / 2 + ny * off;
        s += `<text x="${r3(mx)}" y="${r3(my)}" transform="rotate(${r3(ang)} ${r3(mx)} ${r3(my)})" text-anchor="middle" dominant-baseline="middle" font-family="${P.font}" font-size="0.2" fill="${P.new}">${L.toFixed(2)}</text>`;
      }
    });
    plan.notes.forEach((n, i) => { s += noteSVG(n, i, P, false, 0.2); });
    const ty = y0 + hPlan + 0.2, st = stats();
    s += `<line x1="${x0 + 0.5}" y1="${ty}" x2="${x0 + w - 0.5}" y2="${ty}" stroke="#393C5B" stroke-width="0.03"/>`;
    s += `<text x="${x0 + 0.5}" y="${ty + 0.5}" font-family="${P.font}" font-weight="700" font-size="0.36" fill="#393C5B">${esc([m.client, m.project].filter(Boolean).join(' · '))}</text>`;
    s += `<text x="${x0 + 0.5}" y="${ty + 0.95}" font-family="${P.font}" font-size="0.24" fill="#1D2033">${esc([`${m.drawing || 'Drawing'}, Rev ${m.rev || '-'}`, m.site, m.drawnBy ? 'Drawn by ' + m.drawnBy : '', m.date, scaleText].filter(Boolean).join(' · '))}</text>`;
    if (m.note) s += `<text x="${x0 + 0.5}" y="${ty + 1.35}" font-family="${P.font}" font-size="0.2" fill="#6B6B6B">${esc(m.note)}</text>`;
    const ly = ty + 1.8;
    [['existing', 'Existing wall'], ['new', `New wall (${st.nl.toFixed(1)} m)`], ['remove', `Wall to remove (${st.rl.toFixed(1)} m)`]].forEach(([k, t], j) => {
      const xx = x0 + 0.5 + j * 4.2;
      s += `<line x1="${xx}" y1="${ly - 0.08}" x2="${xx + 0.7}" y2="${ly - 0.08}" stroke="${P[k]}" stroke-width="0.14" ${k === 'remove' ? 'stroke-dasharray="0.2 0.14"' : ''}/><text x="${xx + 0.9}" y="${ly}" font-family="${P.font}" font-size="0.22" fill="#1D2033">${t}</text>`;
    });
    const sbx = x0 + w - 5.5, sby = ty + 1.75;
    for (let i = 0; i < 5; i++) s += `<rect x="${sbx + i}" y="${sby - 0.12}" width="1" height="0.12" fill="${i % 2 ? '#FFFFFF' : '#393C5B'}" stroke="#393C5B" stroke-width="0.015"/>`;
    s += `<text x="${sbx}" y="${sby - 0.22}" font-family="${P.font}" font-size="0.18" fill="#1D2033">0</text><text x="${sbx + 5}" y="${sby - 0.22}" text-anchor="end" font-family="${P.font}" font-size="0.18" fill="#1D2033">5 m</text>`;
    if (aspect) s += `<rect x="${r3(x0 + 0.15)}" y="${r3(y0 + 0.15)}" width="${r3(w - 0.3)}" height="${r3(h - 0.3)}" fill="none" stroke="#393C5B" stroke-width="0.04"/>`;
    return s + '</svg>';
  }
  function fname(ext) { const m = plan.meta, slug = v => String(v || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, ''); return `${slug(m.project) || 'floor-plan'}-${slug(m.drawing) || 'layout'}-Rev${slug(m.rev)}.${ext}`; }
  function offer(filename, blob) {
    const url = URL.createObjectURL(blob), a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }
  // A3 inside 10 mm margins. 'fill' enlarges the plan to fill the sheet and states the scale that gives;
  // 'standard' uses the largest standard scale that fits, for measuring off with a scale rule.
  function printA3(mode) {
    const { w, h } = sheetSize(), sheets = [['landscape', 400, 277], ['portrait', 277, 400]];
    let best = null, svgText;
    if (mode === 'fill') {
      sheets.forEach(([orient, sw, sh]) => { const sc = Math.max(w / sw, h / sh) * 1000; if (!best || sc < best.sc) best = { sc, orient, sw, sh }; });
      const label = best.sc >= 10 ? Math.round(best.sc) : best.sc.toFixed(1);
      best.text = `1:${label}`;
      svgText = buildSVG(`Scale 1:${label} at A3, fitted to page`, `width="${best.sw}mm" height="${best.sh}mm"`, best.sw / best.sh);
    } else {
      [20, 25, 50, 100, 200, 250, 500, 1000].some(sc => {
        const wm = w * 1000 / sc, hm = h * 1000 / sc, fitIn = sheets.find(([, sw, sh]) => wm <= sw && hm <= sh);
        if (fitIn) { best = { sc, wm, hm, orient: fitIn[0], text: `1:${sc}` }; return true; }
        return false;
      });
      if (!best) { flash('This plan is too big to print on A3 at a standard scale.'); return; }
      svgText = buildSVG(`Scale 1:${best.sc} at A3`, `width="${best.wm.toFixed(1)}mm" height="${best.hm.toFixed(1)}mm"`);
    }
    const fr = document.createElement('iframe');
    fr.setAttribute('aria-hidden', 'true');
    fr.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
    document.body.appendChild(fr);
    const d = fr.contentDocument;
    d.open();
    d.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(fname('pdf').replace('.pdf', ''))}</title><style>@page{size:A3 ${best.orient};margin:10mm}html,body{margin:0;padding:0;background:#fff}svg{display:block;margin:0 auto}</style></head><body>${svgText}</body></html>`);
    d.close();
    setTimeout(() => {
      try { fr.contentWindow.focus(); fr.contentWindow.print(); }
      catch (err) { flash('Printing is blocked in this browser. Use Export, Drawing as SVG, and print that at A3.'); }
      setTimeout(() => fr.remove(), 60000);
    }, 250);
    flash(`Printing at ${best.text}, A3 ${best.orient}. In the print dialog choose A3 and 100% (actual size); pick Save as PDF for a file.`);
  }
  function exportPNG() {
    const s = buildSVG(), vb = s.match(/width="(\d+)" height="(\d+)"/), W = +vb[1] * 1.6, H = +vb[2] * 1.6;
    const img = new Image();
    img.onload = () => { const c = document.createElement('canvas'); c.width = W; c.height = H; const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, W, H); g.drawImage(img, 0, 0, W, H); c.toBlob(b => offer(fname('png'), b), 'image/png'); };
    img.onerror = () => flash('Could not build the PNG. Try the SVG export instead.');
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(s);
  }

  /* ---------- boot ---------- */
  svg.addEventListener('pointerdown', onDown);
  svg.addEventListener('pointermove', onMove);
  svg.addEventListener('pointerup', onUp);
  svg.addEventListener('pointercancel', onUp);
  svg.addEventListener('dblclick', onDbl);
  svg.addEventListener('wheel', onWheel, { passive: false });
  const onCtx = e => e.preventDefault();
  svg.addEventListener('contextmenu', onCtx);
  window.addEventListener('keydown', onKey);
  window.addEventListener('keyup', onKeyUp);

  if (opts.draftKey && canEdit) {
    try {
      const d = JSON.parse(load(opts.draftKey) || 'null');
      if (d && d.plan && d.base === baseStamp && JSON.stringify(d.plan) !== snapshot()) {
        const original = snapshot();
        plan = normalisePlan(d.plan); dirty = true;
        showBanner('Restored changes you had not saved on this device.', 'Discard them', () => { undo = []; redo = []; plan = JSON.parse(original); dirty = false; unstore(opts.draftKey); setStatus(); render(); });
      }
    } catch (e) { /* ignore a broken draft */ }
  }
  titleBar(); setStatus(); setTool('select'); fit();
  const ro = new ResizeObserver(() => { if (userView) render(); else fit(); });
  ro.observe(el('stage'));

  return {
    isDirty: () => dirty && !readOnly,
    destroy() {
      clearTimeout(autoT); clearTimeout(flashT); ro.disconnect();
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onDocClick);
      root.innerHTML = ''; root.classList.remove('fp');
    },
  };
}
